import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import InputField from '../common/InputField';
import Button from '../common/Button';
import SideDrawer from '../common/SideDrawer';
import { handleKeyDown, validateFieldWithSchema } from '../../utils/util';
import { set, z } from "zod";
import { InputType, IsFieldValid } from '../../shared/interfaces';
import type { AutoCompleteOption } from '../../shared/interfaces';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { GameType } from '../../shared/enume';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { useNavigate } from 'react-router-dom';
import type { Game } from '../../shared/interfaces';

interface GameFormData {
    gameType: string;
    teamA: string;
    teamB?: string;
    teamC?: string;
    overs?: number | null;
}

interface GameFormProps {
    isOpen: boolean;
    gameType: string;
    onClose: (value: boolean) => void;
    editGame?: Game | null;
}

/** Disallow leading spaces while typing; use trim on blur / submit for trailing. */
const stripLeadingSpaces = (s: string) => s.replace(/^\s+/, '');

const CreateGameDialog: React.FC<GameFormProps> = ({ isOpen, gameType, onClose, editGame = null }) => {
    const [isProcessing, setIsProcessing] = useState<boolean | undefined>(false);
    const [formData, setFormData] = useState<GameFormData>({
        teamA: '',
        teamB: '',
        teamC: '',
        gameType: gameType,
        overs: null as number | null
    });

    const navigate = useNavigate();

    const teamARef = useRef<HTMLInputElement>(null);
    const teamBRef = useRef<HTMLInputElement>(null);
    const oversRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (isOpen) {
            if (editGame) {
                // Edit: fill form with existing game values
                setFormData({
                    teamA: editGame.teamA ?? '',
                    teamB: editGame.teamB ?? '',
                    teamC: editGame.teamC ?? '',
                    gameType: editGame.gameType ?? gameType,
                    overs: editGame.overs ?? null
                });
            } else {
                // Create: start empty
                setFormData({
                    teamA: '',
                    teamB: '',
                    teamC: '',
                    gameType: gameType,
                    overs: null
                });
            }
            setDirtyFields({
                teamA: false,
                teamB: false,
                teamC: false,
                gameType: false,
                overs: false,
            });
            setIsFieldValid({
                teamA: { isValid: true, message: "" },
                teamB: { isValid: true, message: "" },
                teamC: { isValid: true, message: "" },
                gameType: { isValid: true, message: "" },
                overs: { isValid: true, message: "" },
            });
            const focusTimer = setTimeout(() => {
                teamARef.current?.focus();
            }, 350);
            return () => clearTimeout(focusTimer);
        } else {
            // Reset form when drawer closes as well
            setFormData({
                teamA: '',
                teamB: '',
                teamC: '',
                gameType: gameType,
                overs: null
            });
            setDirtyFields({
                teamA: false,
                teamB: false,
                teamC: false,
                gameType: false,
                overs: false,
            });
            setIsFieldValid({
                teamA: { isValid: true, message: "" },
                teamB: { isValid: true, message: "" },
                teamC: { isValid: true, message: "" },
                gameType: { isValid: true, message: "" },
                overs: { isValid: true, message: "" },
            });
        }
    }, [isOpen, gameType, editGame]);


    interface FieldsBoolean {
        teamA: boolean,
        teamB: boolean,
        teamC: boolean,
        gameType: boolean,
        overs: boolean,
        [key: string]: any; // Add index signature to allow string keys
    }

    interface FieldsIsValid {
        teamA: IsFieldValid,
        teamB: IsFieldValid,
        teamC: IsFieldValid,
        gameType: IsFieldValid,
        overs: IsFieldValid,
        [key: string]: any; // Add index signature to allow string keys
    }

    const [dirtyFields, setDirtyFields] = useState<FieldsBoolean>({
        teamA: false,
        teamB: false,
        teamC: false,
        gameType: false,
        overs: false,
    });

    const [isFieldValid, setIsFieldValid] = useState<FieldsIsValid>(
        {
            teamA: { isValid: true, message: "" },
            teamB: { isValid: true, message: "" },
            teamC: { isValid: true, message: "" },
            gameType: { isValid: true, message: "" },
            overs: { isValid: true, message: "" },
        }
    );


    // Session: Team + Overs only (no Team B on form). One day / test: Team A + Team B required.
    const getGameFormSchema = () => {
        const teamA = z.string({ required_error: "Team A is required" }).min(1, "Team A is required").max(20);
        const teamBRequired = z.string({ required_error: "Team B is required" }).min(1, "Team B is required").max(20);
        const teamBSession = z.string().max(20); // not shown on form; always '' or loaded edit value
        const teamC = z.string().optional();
        const gameTypeEnum = z.enum(["session", "oneday", "test"]);

        if (gameType.toLowerCase() === GameType.session.toLowerCase()) {
            return z.object({
                teamA,
                teamB: teamBSession,
                teamC,
                gameType: gameTypeEnum,
                overs: z.coerce.number({ required_error: "Overs is required" }).min(1, "Overs is required").max(999),
            });
        }
        return z.object({
            teamA,
            teamB: teamBRequired,
            teamC,
            gameType: gameTypeEnum,
            overs: z.coerce.number().min(0).max(999).optional(),
        });
    };

    const gameFormSchema = getGameFormSchema();

    const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'teamA' || name === 'teamB') {
            const trimmed = value.trim();
            if (trimmed !== value) {
                setFormData((prev) => ({ ...prev, [name]: trimmed }));
            }
        }
        setDirtyFields((prev: FieldsBoolean) => ({
            ...prev,
            [name]: true
        }));
    }

    // Helper function to validate team name conflicts
    const validateTeamNameConflict = (teamA: string, teamB: string, gameType: string): { isValid: boolean; message: string } => {
        if (!teamA || !teamB || teamA.trim() === '' || teamB.trim() === '') {
            return { isValid: true, message: '' };
        }

        const teamAFirstLetter = teamA.trim().charAt(0).toUpperCase();
        const teamBFirstLetter = teamB.trim().charAt(0).toUpperCase();

        // For OneDay games: Both teams should not start with the same letter
        if (gameType.toLowerCase() === GameType.oneday.toLowerCase()) {
            if (teamAFirstLetter === teamBFirstLetter) {
                return {
                    isValid: false,
                    message: `Team A and Team B cannot start with the same letter (${teamAFirstLetter})`
                };
            }
        }

        // For Test games: Both teams should not start with the same letter
        // Also, if no team starts with D, consider D as well (both should not start with D)
        if (gameType.toLowerCase() === GameType.test.toLowerCase()) {
            if (teamAFirstLetter === teamBFirstLetter) {
                return {
                    isValid: false,
                    message: `Team A and Team B cannot start with the same letter (${teamAFirstLetter})`
                };
            }
            // If Team A doesn't start with D, Team B should not start with D either
            if (teamAFirstLetter !== 'D' && teamBFirstLetter === 'D') {
                return {
                    isValid: false,
                    message: 'Team B cannot start with D when Team A does not start with D'
                };
            }
            // If Team B doesn't start with D, Team A should not start with D either
            if (teamBFirstLetter !== 'D' && teamAFirstLetter === 'D') {
                return {
                    isValid: false,
                    message: 'Team A cannot start with D when Team B does not start with D'
                };
            }
        }

        return { isValid: true, message: '' };
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        let processedValue: string | number | null = value;

        if (name === 'teamA' || name === 'teamB') {
            processedValue = stripLeadingSpaces(value);
        }
        
        // For Overs field in Session games, restrict to numbers only and max 999
        if (name === 'overs' && gameType.toLowerCase() === GameType.session.toLowerCase()) {
            // Remove any non-digit characters
            const numericValue = value.replace(/\D/g, '');
            // Limit to 999
            if (numericValue && parseInt(numericValue) > 999) {
                processedValue = 999;
            } else if (numericValue === '') {
                processedValue = null;
            } else {
                processedValue = parseInt(numericValue) || null;
            }
        }

        // Update form data
        const updatedFormData = {
            ...formData,
            [name]: processedValue
        };
        setFormData(updatedFormData);

        // Mark the field as dirty
        setDirtyFields((prev: FieldsBoolean) => ({
            ...prev,
            [name]: true
        }))

        // Validate the field using zod and update isFieldValid accordingly
        setIsFieldValid((prev: FieldsIsValid) => {
            // Convert processedValue to string for validation (overs can be number or null)
            const valueForValidation = processedValue === null || processedValue === undefined 
                ? '' 
                : String(processedValue);
            const { isValid, message } = validateFieldWithSchema(
                name,
                valueForValidation,
                updatedFormData,
                gameFormSchema
            );

            // Additional validation for team name conflicts (for OneDay and Test games)
            let finalIsValid = isValid;
            let finalMessage = message;

            if ((name === 'teamA' || name === 'teamB') && 
                (gameType.toLowerCase() === GameType.oneday.toLowerCase() || gameType.toLowerCase() === GameType.test.toLowerCase())) {
                const teamConflict = validateTeamNameConflict(
                    name === 'teamA' ? valueForValidation : updatedFormData.teamA || '',
                    name === 'teamB' ? valueForValidation : updatedFormData.teamB || '',
                    gameType
                );
                
                if (!teamConflict.isValid) {
                    finalIsValid = false;
                    finalMessage = teamConflict.message;
                }
            }

            return {
                ...prev,
                [name]: { isValid: finalIsValid, message: finalMessage }
            };
        });

    }

    /**
     * Iterates through all fields in formData and sets isFieldValid for each field using the provided schemas.
     */
    const validateAllFields = (formData: GameFormData) => {
        // Validate main fields
        const mainResult = gameFormSchema.safeParse(formData);
        let mainFieldValid: Partial<FieldsIsValid> = {};

        if (!mainResult.success) {
            for (const issue of mainResult.error.issues) {
                mainFieldValid[issue.path[0] as string] = {
                    isValid: false,
                    message: issue.message,
                };
            }
        }
    };

    // Extract submit logic to be called from both form submit and Enter key
    const submitForm = () => {
        const teamAT = (formData.teamA ?? '').trim();
        const teamBT = (formData.teamB ?? '').trim();
        const normalized: GameFormData = {
            ...formData,
            teamA: teamAT,
            teamB: teamBT,
        };

        if (teamAT !== formData.teamA || teamBT !== (formData.teamB ?? '')) {
            setFormData(normalized);
        }

        // For session games, ensure both teamA and overs are provided
        if (gameType.toLowerCase() === GameType.session.toLowerCase()) {
            if (!teamAT) {
                setDirtyFields((prev) => ({ ...prev, teamA: true }));
                setIsFieldValid((prev) => ({
                    ...prev,
                    teamA: { isValid: false, message: "Team name is required" }
                }));
            }
            if (formData.overs === null || formData.overs === undefined || formData.overs < 1) {
                setDirtyFields((prev) => ({ ...prev, overs: true }));
                setIsFieldValid((prev) => ({
                    ...prev,
                    overs: { isValid: false, message: "Overs is required (minimum 1)" }
                }));
            }
            // If either field is invalid, don't submit
            if (!teamAT || formData.overs === null || formData.overs === undefined || formData.overs < 1) {
                return;
            }
        }

        // Validate team name conflicts for OneDay and Test games
        if ((gameType.toLowerCase() === GameType.oneday.toLowerCase() || gameType.toLowerCase() === GameType.test.toLowerCase()) &&
            teamAT && teamBT) {
            const teamConflict = validateTeamNameConflict(teamAT, teamBT, gameType);
            if (!teamConflict.isValid) {
                setDirtyFields((prev) => ({
                    ...prev,
                    teamA: true,
                    teamB: true,
                }));
                setIsFieldValid((prev) => ({
                    ...prev,
                    teamA: { isValid: false, message: teamConflict.message },
                    teamB: { isValid: false, message: teamConflict.message },
                }));
                return;
            }
        }

        // Validate main form fields (trimmed team names)
        const mainValidation = gameFormSchema.safeParse(normalized);

        if (!mainValidation.success) {
            validateAllFields(normalized);
            // Mark all fields as dirty to show errors
            setDirtyFields((prev) => ({
                ...prev,
                teamA: true,
                teamB: true,
                teamC: true,
                overs: true,
                gameType: true,
            }));
            return;
        }

        setIsProcessing(true);

        const gameData = {
            teamA: teamAT,
            teamB: teamBT,
            teamC: gameType.toLowerCase() === GameType.test.toLowerCase() ? "Draw" : formData.teamC,
            gameType: formData.gameType,
            overs: formData.overs,
        };

        if (editGame?.id) {
            apiService.put(apiRoutes.games.update(editGame.id), gameData, { withCredentials: true })
                .then((updatedGame) => {
                    setIsProcessing(false);
                    onClose(false);
                    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Game updated successfully!', type: 'success' } }));
                    if (updatedGame && (updatedGame as any).id) {
                        navigate(`/game/transactions/${editGame.id}`, { state: { gameDetails: updatedGame } });
                    }
                })
                .catch(err => {
                    setIsProcessing(false);
                    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Error: ' + (err?.message || 'Failed to update game'), type: 'error' } }));
                });
        } else {
            apiService.post(apiRoutes.games.create, gameData, { withCredentials: true })
                .then((createdGame) => {
                    setIsProcessing(false);
                    onClose(false);
                    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Game created successfully!', type: 'success' } }));
                    if (createdGame && (createdGame as any).id) {
                        navigate(`/game/transactions/${(createdGame as any).id}`, { state: { gameDetails: createdGame } });
                    }
                })
                .catch(err => {
                    setIsProcessing(false);
                    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Error: ' + (err?.message || 'Failed to create game'), type: 'error' } }));
                });
        }
    };

    // Handle Enter key for Team A — session: go to Overs (Team B optional); else Team B
    const handleTeamAKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (gameType.toLowerCase() === GameType.session.toLowerCase()) {
                oversRef.current?.focus();
            } else {
                teamBRef.current?.focus();
            }
        }
    }

    // Handle Enter key for Team B - submit the form
    const handleTeamBKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitForm();
        }
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        submitForm();
    };

    return (
        <SideDrawer isOpen={isOpen} onClose={() => onClose(false)} title={editGame ? "Edit Game" : "Create Game"} width="w-96">
            <form onSubmit={handleSubmit} className="w-full">
                <div className="space-y-4">
                    <div className="flex flex-col">
                        <label className="block font-medium text-sm text-gray-700 mb-1">
                            {gameType.toLowerCase() === GameType.session.toLowerCase() ? 'Team' : 'Team A'}
                            <span className="text-red-500">*</span>
                        </label>
                        <input 
                            ref={teamARef} 
                            type="text" 
                            id="teamA" 
                            name="teamA" 
                            onKeyDown={handleTeamAKeyDown} 
                            value={formData.teamA} 
                            onChange={handleChange} 
                            onBlur={handleBlur} 
                            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                !isFieldValid.teamA.isValid && dirtyFields.teamA ? "border-2 border-red-600" : "border-gray-300"
                            }`}
                        />
                        <div className={`text-red-500 text-xs mt-1 ${!isFieldValid.teamA.isValid && dirtyFields.teamA ? "block" : "hidden"}`}>
                            {isFieldValid.teamA.message}            
                        </div>
                    </div>

                    {gameType.toLowerCase() !== GameType.session.toLowerCase() && (
                    <div className="flex flex-col">
                        <label className="block font-medium text-sm text-gray-700 mb-1">
                            Team B<span className="text-red-500">*</span>
                        </label>
                        <input 
                            ref={teamBRef}
                            type="text" 
                            id="teamB" 
                            name="teamB" 
                            onKeyDown={handleTeamBKeyDown} 
                            value={formData.teamB ?? ''} 
                            onChange={handleChange} 
                            onBlur={handleBlur} 
                            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                !isFieldValid.teamB.isValid && dirtyFields.teamB ? "border-2 border-red-600" : "border-gray-300"
                            }`}
                        />
                        <div className={`text-red-500 text-xs mt-1 ${!isFieldValid.teamB.isValid && dirtyFields.teamB ? "block" : "hidden"}`}>
                            {isFieldValid.teamB.message}            
                        </div>
                    </div>
                    )}

                    {gameType.toLowerCase() === GameType.test.toLowerCase() && (
                        <div className="flex flex-col">
                            <label className="block font-medium text-sm text-gray-700 mb-1">Team C</label>
                            <input 
                                type="text" 
                                id="teamC" 
                                name="teamC" 
                                value="Draw" 
                                disabled 
                                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 opacity-75 cursor-not-allowed" 
                            />
                        </div>
                    )}

                    {gameType.toLowerCase() === GameType.session.toLowerCase() && (
                        <div className="flex flex-col">
                            <label className="block font-medium text-sm text-gray-700 mb-1">
                                Overs<span className="text-red-500">*</span>
                            </label>
                            <input 
                                ref={oversRef}
                                type="text" 
                                id="overs" 
                                name="overs" 
                                maxLength={3}
                                required
                                onKeyDown={(e) => {
                                    // Allow: backspace, delete, tab, escape, enter, arrow keys, home, end, and numbers
                                    if (!/[0-9]/.test(e.key) && 
                                        !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key) &&
                                        !(e.ctrlKey || e.metaKey)) {
                                        e.preventDefault();
                                    }
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (formData.teamA?.trim() &&
                                            formData.overs != null && formData.overs >= 1) {
                                            submitForm();
                                        } else {
                                            setDirtyFields((prev) => ({
                                                ...prev,
                                                teamA: true,
                                                overs: true
                                            }));
                                        }
                                    }
                                }} 
                                value={formData.overs !== null && formData.overs !== undefined ? String(formData.overs) : ""} 
                                onChange={handleChange} 
                                onBlur={handleBlur} 
                                placeholder="1-999"
                                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    !isFieldValid.overs.isValid && dirtyFields.overs ? "border-2 border-red-600" : "border-gray-300"
                                }`}
                            />
                            <div className={`text-red-500 text-xs mt-1 ${!isFieldValid.overs.isValid && dirtyFields.overs ? "block" : "hidden"}`}>
                                {isFieldValid.overs.message}            
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-2 pt-6 mt-6 border-t border-gray-200">
                    <Button 
                        type="submit"
                        isProcessing={isProcessing}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {editGame ? "Update Game" : "Save Game"}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => onClose(false)}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </SideDrawer>
    );
};

export default CreateGameDialog;